import { find } from '@execonline-inc/collections';
import { Chip, Typography } from '@execonline-inc/execonline-ui';
import { Maybe } from 'maybeasy';
import { T } from '../../../../../../../Translations';
import { findLink } from '../../../../../../../LinkyLinky';
import { ExperienceResource } from '../../Types';
import { CombinedExperience } from './useFetchMultipleExperienceDetails';
import ClockIcon from '../../../../../../Cutesies/ClockIcon';
import CheckmarkCircleIcon from '../../../../../../Cutesies/CheckmarkCircleIcon';
import DownloadIcon from '../../../../../../Cutesies/DownloadIcon';

interface Props {
  basicExperiences: ExperienceResource[];
  combinedExperiences: CombinedExperience[];
  loadingDetails: boolean;
  detailsError: Maybe<string>;
  totalCount: number;
}

const emptyFragment = () => <></>;

const ProgramsTab: React.FC<Props> = ({
  basicExperiences,
  combinedExperiences,
  loadingDetails,
  detailsError,
  totalCount,
}) => {
  const hasError = detailsError.map(() => true).getOrElse(() => false);

  if (hasError) {
    return detailsError
      .map((error) => (
        <div key="error" className="flex items-center justify-center py-12">
          <Typography variant="bodyMedium" className="text-destructive">
            {error}
          </Typography>
        </div>
      ))
      .getOrElse(emptyFragment);
  }

  if (basicExperiences.length === 0) {
    return (
      <Typography variant="bodySmall">
        <T kind="This section includes {{count}} programs" count={totalCount} />
      </Typography>
    );
  }

  return (
    <div className="space-y-3 max-h-[57vh] overflow-y-auto px-11">
      {basicExperiences.map((basicExp) => {
        const combined = find(
          (c: CombinedExperience) => c.basic.payload.id === basicExp.payload.id,
          combinedExperiences,
        );
        const isLoading = loadingDetails && combined.isNothing();

        return (
          <div
            key={basicExp.payload.id}
            className="border-border flex border-b border-solid py-10 last:border-b-0"
            data-intercom-target={`curated-section-program-${basicExp.payload.id}`}
          >
            {/* Left Side - 2/3 width */}
            <div className="basis-2/3">
              {/* Title */}
              <Typography
                variant="bodyLarge"
                fontWeight="semibold"
                className="mb-3 text-foreground"
              >
                {basicExp.payload.title}
              </Typography>

              {/* Hours Display with Icons */}
              <div className="flex mb-5">
                {basicExp.payload.total_hours
                  .map((hours) => (
                    <Chip
                      key="total-hours"
                      radius="xl"
                      color="light-gray"
                      className="mr-2 px-3 py-1"
                    >
                      <Typography color="dark" variant="caption" className="flex items-center">
                        <span className="mr-2">
                          <ClockIcon />
                        </span>
                        <span>
                          <T kind="{{count}} Hour" count={hours} />
                        </span>
                      </Typography>
                    </Chip>
                  ))
                  .getOrElse(() => (
                    <></>
                  ))}
                {combined
                  .andThen((c) => c.detail)
                  .andThen((d) => d.payload.hours_per_week)
                  .map((hoursPerWeek) => (
                    <Chip
                      key="hours-per-week"
                      radius="xl"
                      color="light-gray"
                      className="mr-2 px-3 py-1"
                    >
                      <Typography color="dark" variant="caption" className="flex items-center">
                        <span className="mr-2 w-[15px] h-[15px]">
                          <CheckmarkCircleIcon />
                        </span>
                        <span>
                          <T kind="{{count}} Hour_per_week" count={hoursPerWeek} />
                        </span>
                      </Typography>
                    </Chip>
                  ))
                  .getOrElse(emptyFragment)}
              </div>

              {/* Description - show skeleton while loading */}
              {isLoading ? (
                <div className="mb-4 space-y-2">
                  <div className="h-4 bg-muted rounded animate-pulse w-full"></div>
                  <div className="h-4 bg-muted rounded animate-pulse w-5/6"></div>
                  <div className="h-4 bg-muted rounded animate-pulse w-4/6"></div>
                </div>
              ) : (
                combined
                  .andThen((c) => c.detail)
                  .map((d) => (
                    <div key="desc" className="mb-4 text-sm text-foreground">
                      {d.payload.description}
                    </div>
                  ))
                  .getOrElse(emptyFragment)
              )}

              {/* Download Overview Link */}
              {combined
                .andThen((c) => c.detail)
                .andThen((d) => findLink('download-overview-pdf', d.links))
                .map((link) => (
                  <a
                    key="download"
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-primary hover:text-primary/80 text-sm"
                  >
                    <DownloadIcon />
                    <T kind="Download Overview" />
                  </a>
                ))
                .getOrElse(emptyFragment)}
            </div>

            {/* Right Side - 2/4 width */}
            <div className="basis-2/4">
              {/* Offered By Section */}
              {basicExp.payload.school_partner_name && (
                <div className="flex flex-col px-10">
                  <div className="mb-2 text-sm font-medium">
                    <T kind="Offered by" />
                  </div>
                  {findLink('logo-on-white', basicExp.links)
                    .map((link) => (
                      <img
                        key="logo"
                        src={link.href}
                        alt={basicExp.payload.school_partner_name}
                        className="max-h-12 max-w-[180px] object-contain mb-2"
                      />
                    ))
                    .getOrElse(() => (
                      <Typography variant="bodyMedium" className="text-foreground">
                        {basicExp.payload.school_partner_name}
                      </Typography>
                    ))}
                </div>
              )}

              {/* Competencies Section */}
              {basicExp.payload.competency_names.length > 0 && (
                <div className="flex flex-col px-10 pt-8 mb-5">
                  <div className="mb-2 text-sm font-medium">
                    <T kind="Competencies" />
                  </div>
                  <div className="flex flex-wrap gap-x-2 gap-y-2">
                    {basicExp.payload.competency_names.map((competency) => (
                      <div
                        key={competency}
                        className="text-foreground border border-foreground break-keep inline-block rounded-md py-1 px-2 text-xs font-medium tracking-tight"
                      >
                        {competency}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProgramsTab;
